/*
 * "Your round is ready" on a phone whose app is closed (owner, 2026-10-02).
 *
 * The server decides who is told (only a player with no device on the game, `games/round_push`)
 * and the service worker words it (`public/sw.js`); this file is the browser's half of the
 * subscription. Three things it holds:
 *
 *  - **Only with a worker, a key and a Push API.** The worker is registered for a built bundle only
 *    (`utils/serviceWorker`), the key comes from `GET /game-config/` (`push_public_key`, empty when
 *    the server cannot sign), and Safari on iOS has the API only inside the installed app. Missing
 *    any of them, `pushSupport` says so and the settings row says nothing.
 *  - **Permission is asked from a press**, never on load: a browser refuses a prompt nobody asked
 *    for, and remembers the refusal.
 *  - **The language rides with the subscription**, because the worker cannot read this page's
 *    storage. A change of language re-sends it (`PushSettings`).
 */
import apiClient from "src/apiClient"

const ENDPOINT = "/users/me/push-subscription/"

/* The VAPID key arrives base64url; `pushManager.subscribe` wants the bytes. */
export const keyToBytes = (base64url) => {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4)
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/")
  const raw = atob(base64)
  return Uint8Array.from(raw, (character) => character.charCodeAt(0))
}

/* "unsupported" (no API, no key), "denied" (the player said no in the browser), or "available". */
export const pushSupport = (publicKey, env = window) => {
  if (!publicKey) return "unsupported"
  const nav = env.navigator
  if (!nav || !("serviceWorker" in nav) || !("PushManager" in env) || !("Notification" in env)) {
    return "unsupported"
  }
  if (env.Notification.permission === "denied") return "denied"
  return "available"
}

const registration = async (nav = navigator) => {
  const found = await nav.serviceWorker.getRegistration()
  if (!found) throw new Error("No service worker")
  return found
}

/* `undefined` with no worker at all (a dev server, a registration that failed) — nothing can be
   switched on there, and the row says nothing; `null` for a worker with no subscription. */
export const currentSubscription = async (nav = navigator) => {
  const found = await nav.serviceWorker.getRegistration()
  if (!found) return undefined
  return found.pushManager.getSubscription()
}

const send = (subscription, language) =>
  apiClient.post(ENDPOINT, { ...subscription.toJSON(), language: language || "" })

/* Ask, subscribe, and tell the server. Resolves to whether the player is now subscribed. */
export const enablePush = async (publicKey, language, { nav = navigator, notification = Notification } = {}) => {
  const permission = await notification.requestPermission()
  if (permission !== "granted") return false
  const reg = await registration(nav)
  const existing = await reg.pushManager.getSubscription()
  const subscription =
    existing || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyToBytes(publicKey) }))
  try {
    await send(subscription, language)
  } catch (error) {
    // The server did not take it (unconfigured, a session gone, no network): a subscription the
    // browser holds and nobody serves would read as "on" at the next mount. Only one made here
    // is undone; one that was already there belongs to whoever registered it.
    if (!existing) await subscription.unsubscribe().catch(() => undefined)
    throw error
  }
  return true
}

/* The server forgets this browser first, then the browser forgets the push service. */
export const disablePush = async ({ nav = navigator } = {}) => {
  const subscription = await currentSubscription(nav)
  if (!subscription) return
  await apiClient.delete(ENDPOINT, { data: { endpoint: subscription.endpoint } })
  await subscription.unsubscribe()
}

export const resendLanguage = async (language, { nav = navigator } = {}) => {
  const subscription = await currentSubscription(nav)
  if (subscription) await send(subscription, language)
}
